import { createContext, useContext, useEffect } from 'react';
import type { AppId, AppParams } from '@omnia/shared';

export type Launch = (id: AppId, params?: AppParams) => void;

/** Opens another app, e.g. Files handing a PDF to the viewer; desktop and phone shells implement it differently. */
export const LaunchContext = createContext<Launch>(() => {});
export const useLaunch = () => useContext(LaunchContext);

interface WindowContextValue {
  params: AppParams;
  setParams: (params: AppParams) => void;
  /** Shown after the app name in the title bar, e.g. the open document's name. */
  setSubtitle: (subtitle: string | null) => void;
  /** While true, closing the window asks before discarding the app's work. */
  setUnsaved: (unsaved: boolean) => void;
  /** Asks the shell to resize the window, e.g. a calculator that grows for its scientific keys; ignored where windows have a fixed size. */
  resize: (update: (size: { width: number; height: number }) => { width: number; height: number }) => void;
}

export const WindowContext = createContext<WindowContextValue>({
  params: {},
  setParams: () => {},
  setSubtitle: () => {},
  setUnsaved: () => {},
  resize: () => {},
});
export const useWindowParams = () => useContext(WindowContext);

/** Puts the open document's name in the window title while the component is mounted. */
export function useWindowSubtitle(subtitle: string | null | undefined) {
  const { setSubtitle } = useContext(WindowContext);
  useEffect(() => {
    setSubtitle(subtitle || null);
    return () => setSubtitle(null);
  }, [subtitle, setSubtitle]);
}

/** Marks the window as holding unsaved work, so the shell confirms before closing it. */
export function useUnsavedChanges(unsaved: boolean) {
  const { setUnsaved } = useContext(WindowContext);
  useEffect(() => {
    setUnsaved(unsaved);
    return () => setUnsaved(false);
  }, [unsaved, setUnsaved]);
}
